/**
 * The container that renders tree node.
 *
 * @author Daniel Lee (dlee@yext.com)
 */
import {TreeNode} from '/src/com/yext/sitesgitstorm/js/src/editor/tree/nodes/TreeNode';
import {TreeNodeShape} from '/src/com/yext/sitesgitstorm/js/src/editor/tree/nodes/shapesJsDoc';
import {TreeSelectMode} from '/src/com/yext/sitesgitstorm/js/src/editor/tree/constants';
import {TreeActionsPortal} from '/src/com/yext/sitesgitstorm/js/src/editor/tree/actions/TreeActionsPortal';

/**
 * @param {{
 *   rootNodeId: string,
 *   nodeIdToNode: !Map<string, TreeNodeShape>,
 *   selectedFileNodeIds: !Set<string>,
 *   expandedNodeIds: !Set<string>,
 *   modifiedFileNodeIds: (!Set<string> | undefined),
 *   focusedFileNodeId: (string | undefined),
 *   hasEditPermission: (boolean | undefined),
 *   treeSelectMode: (string | undefined),
 *   treeActionsMode: (string | undefined),
 *   onDoubleClickDirectory: (function(string) | undefined),
 *   onExpandDirectory: !function(string),
 *   onClickFile: !function(TreeNodeShape),
 *   onAddOrUploadFiles: (function(string) | undefined),
 *   onDeleteFile: (function(TreeNodeShape) | undefined),
 *   onAddDirectory: (function(string) | undefined),
 *   onRenameDirectory: (function(string) | undefined),
 *   onRenameFile: (function(TreeNodeShape) | undefined),
 *   onDeleteDirectory: (function(string) | undefined),
 *   onBulkSelectDirectory: (function(string, boolean) | undefined),
 *   onBulkSelectFile: (function(string, boolean) | undefined),
 * }} props
 * @return {React.ReactNode}
 * @suppress {checkTypes}
 */
export default function FileTree(props) {
  const {
    rootNodeId,
    nodeIdToNode,
    selectedFileNodeIds,
    expandedNodeIds,
    modifiedFileNodeIds,
    focusedFileNodeId,
    hasEditPermission,
    treeSelectMode = TreeSelectMode.NONE,
    treeActionsMode,
    onDoubleClickDirectory,
    onExpandDirectory,
    onClickFile,
    onAddOrUploadFiles,
    onDeleteFile,
    onAddDirectory,
    onRenameDirectory,
    onRenameFile,
    onDeleteDirectory,
    onBulkSelectDirectory,
    onBulkSelectFile,
  } = props;

  const [hoveredRowRef, setHoveredRowRef] = React.useState(undefined);
  const [hoveredRowNodeId, setHoveredRowNodeId] = React.useState(undefined);

  /**
   * @param {{current: {getBoundingClientRect: !Function}}} hoveredRowRef
   * @param {string} nodeId
   */
  const handleOnHoverRow = React.useCallback((hoveredRowRef, nodeId) => {
    setHoveredRowNodeId(nodeId);
    setHoveredRowRef(hoveredRowRef);
  }, []);

  return (
    <div
      className="tree-container tree-node"
      onMouseLeave={() => setHoveredRowNodeId(undefined)}
    >
      <TreeNode
        key={rootNodeId}
        nodeId={rootNodeId}
        nodeIdToNode={nodeIdToNode}
        expandedNodeIds={expandedNodeIds}
        selectedFileNodeIds={selectedFileNodeIds}
        modifiedFileNodeIds={modifiedFileNodeIds}
        focusedFileNodeId={focusedFileNodeId}
        treeActionsMode={treeActionsMode}
        isRootNode={true}
        treeSelectMode={treeSelectMode}
        onExpandDirectory={onExpandDirectory}
        onHoverRow={handleOnHoverRow}
        onDoubleClickDirectory={onDoubleClickDirectory}
        onBulkSelectDirectory={onBulkSelectDirectory}
        onBulkSelectFile={onBulkSelectFile}
        onClickFile={onClickFile}
      />
      {hasEditPermission && treeActionsMode && (
        <TreeActionsPortal
          containingRef={hoveredRowRef}
          mode={treeActionsMode}
          node={
            hoveredRowNodeId !== undefined
              ? nodeIdToNode.get(hoveredRowNodeId)
              : undefined
          }
          onAddOrUploadFiles={onAddOrUploadFiles}
          onAddDirectory={onAddDirectory}
          onDeleteDirectory={
            hoveredRowNodeId !== rootNodeId ? onDeleteDirectory : undefined
          }
          onDeleteFile={onDeleteFile}
          onRenameFile={onRenameFile}
          onRenameDirectory={
            hoveredRowNodeId !== rootNodeId ? onRenameDirectory : undefined
          }
        />
      )}
    </div>
  );
}
